import classNames from 'classnames';
import {enableScroll} from 'core/helpers';
import React, {FC, useEffect, useRef, useState} from 'react';
import style from './style.module.scss';

export const LOADER_THEMES = {
    logo: 'logo',
    primary: 'primary',
    secondary: 'secondary',
} as const;

export const LOADER_MODES = {
    compact: 'compact',
    standard: 'standard',
} as const;

export const LOADER_COLOR = {
    black: 'black',
    red: 'red',
} as const;

export type TLoaderTheme = keyof typeof LOADER_THEMES;
export type TLoaderMode = keyof typeof LOADER_MODES;
export type TLoaderColor = keyof typeof LOADER_COLOR;

export const LOADER_DISABLED_TIME = 500;

export interface ILoaderProps {
    className?: string;
    theme?: TLoaderTheme;
    mode?: TLoaderMode;
    color?: TLoaderColor;
}

export interface ILogoLoaderProps {
    className?: string;
    isLoading?: boolean;
    isNotForReload?: boolean;
    fixed?: boolean;
}

export const LegacyLoader: FC<ILoaderProps> = ({
    className,
    theme = LOADER_THEMES.primary,
    mode = LOADER_MODES.standard,
    color = LOADER_COLOR.black,
}) => (
    <div
        className={classNames(style.loader, className, {
            [style[theme]]: Boolean(theme),
            [style[mode]]: Boolean(mode),
            [style[color]]: Boolean(color),
        })}
    />
);

export const LogoLoader: FC<ILogoLoaderProps> = ({
    fixed = false,
    className,
    isLoading = true,
    isNotForReload = false,
}) => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const [isDisabled, setIsDisabled] = useState<boolean>(isNotForReload);

    useEffect(() => {
        if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
        }

        if (isLoading && isNotForReload) {
            setIsDisabled(false);
        }

        if (!isLoading) {
            timeoutRef.current = setTimeout(() => {
                setIsDisabled(true);
                enableScroll();
            }, LOADER_DISABLED_TIME);
        }

        return () => {
            clearInterval(timeoutRef.current);
        };
    }, [isLoading, isNotForReload]);

    return (
        <div
            className={classNames(style.logoLoader, className, {
                [style.loaded]: !isLoading,
                [style.disabled]: isDisabled,
                [style.transparent]: isNotForReload,
                [style.logoLoaderFixed]: fixed,
            })}
        >
            <LegacyLoader theme={LOADER_THEMES.logo} />
        </div>
    );
};
